import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { trackProductEvent } from "@/app/infra/product-events"
import { getBaseMintNftEventContext } from "@/app/nft/utils/get-base-nft-event-context"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "@/app/sdk/action-molecules/events/transaction-event"
import { InsufficientFundsError } from "@/app/sdk/errors"
import { MintToast } from "@/app/sdk/toasts/mint-toast"
import { getDropCurrentPhase } from "@/app/utils/get-drop-current-phase"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import { Blockchain } from "@anaconda/core/blockchain/types"
import { FeeType, type MinimalDrop } from "@anaconda/core/drop/types"
import {
  parseUnionAddress,
  validateWalletNetwork,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import Big from "big.js"
import { molecule } from "bunshi"
import type { Address } from "viem"
import { type Hex, pad } from "viem"
import {
  crosschainGasAbi,
  crosschainGasAbiContractAddress,
} from "./abi/crosschain-gas-abi"
import { espressoAbi } from "./abi/espresso-abi"
import { espressoDropAbi } from "./abi/espresso-drop-abi"
import { EspressoMoleculeReatom } from "./espresso-molecule.reatom"

/**
 * Espresso Mint Transaction Molecule (Reatom v1000)
 *
 * Handles minting for Espresso protocol (MultiChain drops)
 * Supports both same-chain and cross-chain minting
 *
 * Migrated from: Jotai atomWithMutation → Reatom action + withAsync
 */

interface MintActionParams {
  drop: MinimalDrop
  quantity?: number
  blockchain?: Blockchain
}

interface MintActionResult {
  txId: Address
  blockchain: Blockchain
  collectionAddress: string
  drop: MinimalDrop
  quantity: number
}

export const EspressoMintTransactionMoleculeReatom = molecule((mol) => {
  const { address: addressAtom } = mol(WalletConnectMoleculeReatom)
  const { reatomChainId } = mol(BlockchainsMoleculeReatom)
  const logger = mol(LoggerMolecule)
  const {
    reatomEspressoContract,
    reatomValidateBalance,
    walletClientAtom,
    publicClientAtom,
  } = mol(EspressoMoleculeReatom)

  const mintAction = action(
    async ({ drop, quantity = 1, blockchain }: MintActionParams) => {
      const address = addressAtom()

      if (!address) {
        throw new Error("Wallet is not connected")
      }

      const targetBlockchain = blockchain ?? drop.blockchain
      const contractAtom = reatomEspressoContract(targetBlockchain)
      const contractAddress = contractAtom()

      if (!contractAddress) {
        throw new Error("Contract address is not available")
      }

      const walletClient = await walletClientAtom()
      const publicClient = await publicClientAtom()

      if (!walletClient || !publicClient) {
        throw new Error("Wallet or public client is not available")
      }

      // Validate network
      const expectedChainId = reatomChainId()(targetBlockchain)
      await wrap(
        // @ts-expect-error different version of viem in the app and thirdweb package leads to this error
        validateWalletNetwork(walletClient, expectedChainId, targetBlockchain),
      )

      const currentPhase = getDropCurrentPhase(drop.phases)

      if (!currentPhase) {
        throw new Error("Current phase is not available")
      }

      // Get price for the target blockchain
      const currentPhasePrice =
        currentPhase.pricesPerChain?.find(
          (p) => p.blockchain === targetBlockchain,
        )?.price ?? currentPhase.price

      if (currentPhasePrice) {
        // Calculate fees per item
        const feesPerItem = drop.fees.reduce((acc, fee) => {
          const feeValue = fee.type === FeeType.FLAT ? fee.amount : 0
          return acc + feeValue
        }, 0)

        const totalPricePerItem = currentPhasePrice.amount
        const basePricePerItem = Big(totalPricePerItem)
          .minus(feesPerItem)
          .toString()

        const totalBasePrice = Big(basePricePerItem).mul(quantity).toString()
        const totalFees = Big(feesPerItem).mul(quantity).toString()
        const totalRequired = Big(totalPricePerItem).mul(quantity).toString()

        // Validate balance
        const validateBalanceAtom = reatomValidateBalance({
          blockchain: targetBlockchain,
          price: {
            amount: totalRequired,
            currency: currentPhasePrice.currency,
          },
        })

        const { isSufficient, balance } = await validateBalanceAtom()

        if (!isSufficient) {
          throw new InsufficientFundsError({
            requiredAmount: totalRequired,
            currentBalance: balance,
            currency: currentPhasePrice.currency,
            basePrice: totalBasePrice,
            fees: totalFees,
            quantity,
            blockchain: targetBlockchain,
          })
        }
      }

      const totalValue = Big(currentPhasePrice.amount).mul(Big(quantity))
      const valueInWei = BigInt(totalValue.mul(Big(10).pow(18)).toString())
      const gas = BigInt(Big(250000).toString())

      // Same-chain mint
      if (drop.blockchain === targetBlockchain) {
        const hash = await wrap(
          // @ts-expect-error different version of viem in the app and thirdweb package leads to this error
          walletClient.writeContract({
            address: parseUnionAddress(drop.id).address as Address,
            abi: espressoDropAbi,
            functionName: "mint",
            args: [address as Address],
            value: valueInWei,
            gas,
            maxFeePerGas: gas,
            maxPriorityFeePerGas: gas,
            account: address as Address,
          }),
        )

        const receipt = await wrap(
          publicClient.waitForTransactionReceipt({ hash }),
        )

        if (receipt.status !== "success") {
          throw new Error("Transaction failed")
        }

        return {
          txId: receipt.transactionHash,
          blockchain: targetBlockchain,
          collectionAddress: drop.id,
          drop,
          quantity,
        }
      }

      // Cross-chain mint
      const recipientBytes32 = pad(address as Hex, { size: 32 })

      let gasInValue = BigInt(Big(250000).toString())
      const domainId =
        targetBlockchain === Blockchain.APECHAIN ? 1000012617 : 33139
      const gasLimit = BigInt(Big(250000).toString())

      try {
        gasInValue = await wrap(
          publicClient.readContract({
            address: crosschainGasAbiContractAddress as Address,
            abi: crosschainGasAbi,
            functionName: "quoteGasPayment",
            args: [domainId, gasLimit],
          }),
        )
      } catch (error) {
        console.error(
          `Error getting crosschain gas value for domain: ${domainId}`,
          error,
        )
      }

      const hash = await wrap(
        // @ts-expect-error different version of viem in the app and thirdweb package leads to this error
        walletClient.writeContract({
          address: contractAddress as Address,
          abi: espressoAbi,
          functionName: "initiateCrossChainNftPurchase",
          args: [recipientBytes32],
          value: valueInWei + gasInValue,
          gas,
          account: address as Address,
        }),
      )

      const receipt = await wrap(
        publicClient.waitForTransactionReceipt({ hash }),
      )

      if (receipt.status !== "success") {
        throw new Error("Transaction failed")
      }

      return {
        txId: receipt.transactionHash,
        blockchain: targetBlockchain,
        collectionAddress: drop.id,
        drop,
        quantity,
      }
    },
    "espressoMintAction",
  ).extend(
    withAsync(),

    // onCall: Called when action starts (replaces onMutate)
    withCallHook((values, [params]) => {
      const { drop, quantity = 1, blockchain } = params

      sonnerToast.custom(
        (id) => <MintToast.Pending drop={drop} id={id} quantity={quantity} />,
        {
          duration: Number.POSITIVE_INFINITY,
        },
      )

      const currentPhase = getDropCurrentPhase(drop.phases)

      const baseTransactionEventContext: EventContext = {
        flow: Flow.Mint,
        ...getBaseMintNftEventContext({
          blockchain: drop.blockchain,
          collectionId: drop.id,
        }),
        ...(currentPhase && {
          type: currentPhase.type,
          startDate: currentPhase.startDate,
          endDate: currentPhase.endDate,
          price: currentPhase.price.amount,
        }),
      }

      logger?.api.pushEvent(TransactionEvent.Started, {
        ...baseTransactionEventContext,
        purchaseBlockchain: blockchain ?? "",
      })
      amplitude.track(TransactionEvent.Started, {
        ...baseTransactionEventContext,
        purchaseBlockchain: blockchain,
      })
    }),
  )

  // onFulfill: Called on success (replaces onSuccess)
  mintAction.onFulfill.extend(
    withCallHook((result) => {
      const { txId, blockchain, collectionAddress, drop, quantity } =
        result.payload

      const context: EventContext = {
        flow: Flow.Mint,
        ...getBaseMintNftEventContext({
          blockchain: drop.blockchain,
          collectionId: drop.id,
        }),
        transactionId: txId,
        quantity: quantity.toString(),
        purchaseBlockchain: blockchain ?? "",
      }

      logger?.api.pushEvent(TransactionEvent.Completed, context)
      amplitude.track(TransactionEvent.Completed, context)
      trackProductEvent("nft_mint_completed", {
        blockchain: drop.blockchain,
        mint_provider: "espresso",
        purchase_blockchain: blockchain,
        quantity,
      })

      const formattedBlockchain =
        drop.blockchain?.charAt(0).toUpperCase() +
        drop.blockchain?.slice(1).toLowerCase()

      const title =
        drop.blockchain !== blockchain
          ? `Congrats! Item will appear on ${formattedBlockchain} soon`
          : undefined

      sonnerToast.dismiss()
      sonnerToast.custom(
        (id) => (
          <MintToast.Success
            drop={drop}
            title={title}
            id={
              collectionAddress
                ? parseUnionAddress(collectionAddress).address
                : drop.id
            }
            quantity={quantity}
          />
        ),
        {
          duration: 4000,
          id: "espresso-mint-success",
        },
      )
    }),
  )

  // onReject: Called on error (replaces onError)
  mintAction.onReject.extend(
    withCallHook(({ error, params: [paramsValue] }) => {
      console.error("Espresso mint failed:", error)

      const { drop, quantity = 1, blockchain } = paramsValue

      const context: EventContext = {
        flow: Flow.Mint,
        ...getBaseMintNftEventContext({
          blockchain: drop.blockchain,
          collectionId: drop.id,
        }),
        error: getTransactionErrorMessage(error),
        quantity: quantity.toString(),
        purchaseBlockchain: blockchain ?? "",
      }

      logger?.api.pushEvent(TransactionEvent.Failed, context)
      amplitude.track(TransactionEvent.Failed, context)

      sonnerToast.dismiss()
      sonnerToast.custom(
        (id) => (
          <MintToast.Error
            drop={drop}
            id={id}
            error={error as Error}
            quantity={quantity}
          />
        ),
        {
          duration: 4000,
          id: "espresso-mint-error",
        },
      )
    }),
  )

  return { mintAction }
})
